import React, { useEffect, useMemo } from 'react';
import { Translate, ValidatedField, translate } from 'react-jhipster';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, FormFeedback, Input, InputGroup, Label, Row } from 'reactstrap';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getOrganizations } from 'app/entities/organization/organization.reducer';
import { reactStrapStyles } from 'app/shared/util/form-utils';
import { Controller, FieldError, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import { DEFAULT_ORGANIZATION } from '../application-user/utils';
import { createEntity, getEntity, reset, updateEntity } from './inventory.reducer';
import InputMask from 'react-input-mask';

export const InventoryUpdate = ({ isReadOnly }: { isReadOnly: boolean }) => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;
  const organizations = useAppSelector(state => state.organization.menuOptions);
  const organizationEntities = useAppSelector(state => state.organization.entities);
  const deviceEntity = useAppSelector(state => state.inventory.entity);
  const loading = useAppSelector(state => state.inventory.loading);
  const updating = useAppSelector(state => state.inventory.updating);
  const updateSuccess = useAppSelector(state => state.inventory.updateSuccess);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
    reset: resetForm,
  } = useForm();

  const handleClose = () => {
    navigate('/inventory' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }
    dispatch(getOrganizations({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  // eslint-disable-next-line complexity
  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    const entity = {
      ...deviceEntity,
      ...values,
      organization: organizationEntities?.find(it => it?.id?.toString() === values?.org?.value?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };
  const defaultValues = useMemo(
    () =>
      isNew
        ? {
            org: organizations?.find(it => it?.label?.toString()?.toUpperCase() === DEFAULT_ORGANIZATION),
          }
        : {
            ...deviceEntity,
            org: organizations?.find(it => it?.value?.toString() === deviceEntity?.organization?.id?.toString()),
          },
    [deviceEntity, isNew, organizations],
  );
  useEffect(() => {
    resetForm({
      ...defaultValues,
    });
  }, [resetForm, defaultValues]);

  return (
    <Card className="jh-card">
      <Row className="justify-content-center">
        <Col md="12">
          <h2
            id="rainspotApp.device.home.createOrEditLabel"
            data-testid="inventory-create-update-heading"
            data-cy="DeviceCreateUpdateHeading"
          >
            <Translate contentKey={isNew ? `rainspotApp.device.home.createLabel` : `rainspotApp.device.home.editLabel`}>
              Create or edit a Device
            </Translate>
          </h2>
        </Col>
      </Row>
      <div className="mt-10">&nbsp;</div>
      <Row className="justify-content-start">
        <Col md="12">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <form onSubmit={vals => void handleSubmit(saveEntity)(vals)}>
              <div className="form-body">
                <ValidatedField
                  label={translate('rainspotApp.device.serialNumber') + '*'}
                  id="device-serialNumber"
                  name="serialNumber"
                  data-testid="serialNumber"
                  data-cy="serialNumber"
                  type="text"
                  disabled={isReadOnly}
                  register={register}
                  error={errors?.serialNumber as FieldError}
                  validate={{
                    required: { value: true, message: translate('entity.validation.required') },
                    minLength: { value: 20, message: translate('rainspotApp.device.validation.serialNumber.minLength') },
                    maxLength: { value: 20, message: translate('rainspotApp.device.validation.serialNumber.maxLength') },
                  }}
                  onChange={e => {
                    e.target.value = e.target.value.toUpperCase();
                  }}
                  onBlur={e => {
                    setValue('serialNumber', e.target.value.toUpperCase());
                  }}
                />
                <div className="d-flex">
                  <div style={{ display: 'flex' }} className="flex-column selectable">
                    <Label for="macAddress">{'Mac Address'}</Label>
                    <Controller
                      control={control}
                      name="macAddress"
                      rules={{
                        validate(number: string | null) {
                          if (number && number.length < 12) {
                            return 'Valid Mac Address is required';
                          }
                        },
                      }}
                      render={({ field, fieldState }) => {
                        return (
                          <>
                            <InputMask
                              disabled={isReadOnly}
                              mask="**-**-**-**-**-**"
                              maskChar="_"
                              value={field.value}
                              onChange={e => field.onChange(e)}
                            >
                              {inputProps => (
                                <Input
                                  {...inputProps}
                                  disabled={isReadOnly}
                                  className={`${fieldState?.error ? 'is-invalid' : ''}`}
                                  data-testid="macAddress"
                                  placeholder="__-__-__-__-__-__"
                                />
                              )}
                            </InputMask>
                            <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>
                          </>
                        );
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex' }} className="flex-column">
                    <Label for="organizationId">{'Organization*'}</Label>
                    <InputGroup className="mb-3" error={!!errors?.org} data-testid="organizationId" data-cy="organizationId">
                      <Controller
                        control={control}
                        data-testid="organizationId"
                        name="org"
                        rules={{
                          required: 'Organization is required',
                        }}
                        render={({ field, fieldState }) => {
                          return (
                            <>
                              <ReactSelect
                                isDisabled={isReadOnly}
                                menuPlacement="auto"
                                classNamePrefix="org"
                                options={organizations}
                                styles={reactStrapStyles({ error: !!fieldState.error?.message })}
                                value={field.value}
                                onChange={(option: any) => {
                                  field.onChange(option);
                                }}
                              />
                            </>
                          );
                        }}
                      />
                      <FormFeedback>{errors?.organizationId?.message as string}</FormFeedback>
                    </InputGroup>
                  </div>
                  <ValidatedField
                    label={translate('rainspotApp.device.imeiNo')}
                    id="device-imeiNo"
                    name="imeiNo"
                    data-cy="imeiNo"
                    data-testid="imeiNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.imeiNo as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                </div>

                <div className="d-flex">
                  <ValidatedField
                    label={translate('rainspotApp.device.microControllerSerialNo')}
                    id="device-microControllerSerialNo"
                    name="microControllerSerialNo"
                    data-cy="microControllerSerialNo"
                    data-testid="microControllerSerialNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.microControllerSerialNumber as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                  <ValidatedField
                    label={translate('rainspotApp.device.microControllerFwNo')}
                    id="device-microControllerFwNo"
                    name="microControllerFwNo"
                    data-cy="microControllerFwNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.microControllerFwNo as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                  <ValidatedField
                    label={translate('rainspotApp.device.wiSunSerialNo')}
                    id="device-wiSunSerialNo"
                    name="wiSunSerialNo"
                    data-cy="wiSunSerialNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.wiSunSerialNo as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                </div>

                <div className="d-flex">
                  <ValidatedField
                    label={translate('rainspotApp.device.wiSunFwNo')}
                    id="device-wiSunFwNo"
                    name="wiSunFwNo"
                    data-cy="wiSunFwNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.wiSunFwNo as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                  <ValidatedField
                    label={translate('rainspotApp.device.cellModuleSerialNo')}
                    id="device-cellModuleSerialNo"
                    name="cellModuleSerialNo"
                    data-cy="cellModuleSerialNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.cellModuleSerialNo as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                  <ValidatedField
                    label={translate('rainspotApp.device.cellModuleFwNo')}
                    id="device-cellModuleFwNo"
                    name="cellModuleFwNo"
                    data-cy="cellModuleFwNo"
                    type="text"
                    disabled={true}
                    register={register}
                    error={errors?.cellModuleFwNo as FieldError}
                    validate={{
                      required: { value: false, message: translate('entity.validation.required') },
                    }}
                  />
                </div>
              </div>
              <hr />
              <div className="justify-content-end d-flex form-footer">
                <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/inventory" replace color="info">
                  <span className="d-none d-md-inline">
                    <Translate contentKey="entity.action.back">Back</Translate>
                  </span>
                </Button>
                &nbsp;
                {!isReadOnly && (
                  <Button
                    color="primary"
                    id="save-entity"
                    data-cy="entityCreateSaveButton"
                    data-testid="save-button"
                    type="submit"
                    disabled={updating}
                  >
                    <Translate contentKey="entity.action.save">Save</Translate>
                  </Button>
                )}
              </div>
            </form>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default InventoryUpdate;
